/* ============================================================================
   Cartas de performance — Airbus AS350 B3 (Arriel 2B1)
   ----------------------------------------------------------------------------
   ORIGEN Y ESTADO DE VERIFICACIÓN

   Estos números fueron digitalizados a mano desde el Manual de Vuelo (RFM) y
   NO están verificados celda por celda. Cada tabla lleva su propio bloque
   `procedencia` con su estado real; la aplicación está obligada a mostrarlo.

   No corrijas un valor "porque parece mal". El único criterio válido para
   cambiar un número de acá es leerlo del RFM. Las anomalías detectadas
   automáticamente se registran en `anomaliasConocidas` y se verifican en los
   tests: cuando alguien las corrija contra el manual, el test va a fallar y va
   a obligar a actualizar este registro. Es deliberado.

   Ver docs/decisiones.md — AD-005.
   ========================================================================== */

const OGE_ALTS = Array.from({ length: 37 }, (_, i) => i * 500);
const OGE_TEMPS = [-20, -10, 0, 10, 20, 30, 40, 50];
const OGE_ROWS = [
  [2800,2800,2800,2800,2800,2775,2750,2610],[2800,2800,2800,2800,2790,2765,2740,2560],
  [2800,2800,2800,2800,2775,2750,2690,2520],[2800,2800,2800,2790,2765,2725,2640,2460],
  [2800,2800,2800,2775,2745,2710,2580,2420],[2800,2800,2790,2760,2725,2680,2540,2370],
  [2800,2800,2775,2750,2710,2630,2490,null],[2800,2790,2760,2725,2690,2580,2440,null],
  [2800,2775,2740,2710,2650,2530,2390,null],[2790,2760,2725,2690,2600,2480,2340,null],
  [2774,2750,2710,2650,2550,2440,2290,null],[2763,2725,2690,2610,2500,2400,2250,null],
  [2745,2710,2670,2560,2460,2350,2210,null],[2730,2690,2605,2520,2420,2300,2170,null],
  [2715,2670,2560,2470,2370,2250,2120,null],[2700,2620,2520,2420,2320,2210,2070,null],
  [2680,2570,2470,2380,2280,2170,null,null],[2620,2520,2420,2320,2240,2120,null,null],
  [2580,2470,2380,2280,2190,2070,null,null],[2520,2420,2340,2240,2150,2040,null,null],
  [2480,2380,2290,2200,2110,1990,null,null],[2420,2320,2250,2150,2060,1950,null,null],
  [2370,2280,2200,2110,2020,1910,null,null],[2320,2240,2160,2070,1980,1870,null,null],
  [2280,2190,2110,2025,1940,1840,null,null],[2240,2150,2070,1990,1900,1800,null,null],
  [2180,2110,2030,1940,1860,null,null,null],[2140,2070,1990,1910,1820,null,null,null],
  [2100,2030,1950,1870,1780,null,null,null],[2060,1995,1910,1840,1750,null,null,null],
  [2020,1950,1870,1800,1720,null,null,null],[1980,1905,1830,1760,1680,null,null,null],
  [1945,1865,1790,1720,1640,null,null,null],[1900,1825,1755,1680,1610,null,null,null],
  [1860,1785,1720,1650,1570,null,null,null],[1820,1750,1680,1615,1530,null,null,null],
  [1780,1720,1650,1580,null,null,null,null],
];
// Carta IGE real (AS350 B3, "5.6 HOVER IN GROUND EFFECT", techo 2250 kg en carga interna, Hp hasta 23 000 ft).
// IMPORTANTE: es un nomograma de líneas diagonales de OAT, no una tabla de celdas como la OGE. Los valores de
// abajo son una aproximación analítica leída visualmente de la carta (no confirmada con puntos de anclaje del
/**
 * Carta IGE — estacionario DENTRO del efecto suelo, AS 350 B3 Plus / Arriel 2B1.
 *
 * Valores de la "Tabla de Peso Máximo por Aeronave" del operador, cotejados
 * contra la Figura 5 del RFM (IGE Hovering Flight Performance).
 *
 * COBERTURA
 * ---------
 * De 1 000 a 22 000 ft y de +10 a +35 °C. El RFM cubre de 0 a 23 000 ft y de
 * −40 °C a ISA+35, así que por debajo de +10 °C la app dirá "fuera de carta":
 * es un hueco de la tabla del operador, no un límite de la aeronave.
 *
 * CELDAS ANULADAS
 * ---------------
 * Cinco celdas violan las dos reglas físicas de esta carta —el peso máximo no
 * puede subir con la temperatura ni con la altitud— y las cinco lo hacen
 * PERMITIENDO MÁS PESO, que es la dirección peligrosa:
 *
 *   15 000 ft / 20 °C = 2250   +420 kg sobre los 15 °C de la misma altitud
 *   16 500 ft / 20 °C = 1675   +25 kg sobre los 15 °C
 *   19 500 ft / 20 °C = 1430   +10 kg sobre los 15 °C
 *   21 000 ft / 20 °C = 1350   +20 kg sobre los 15 °C
 *   22 000 ft / 10 °C = 1430   +20 kg sobre los 21 000 ft
 *
 * Quedan en null. Un hueco manda al manual; un número que se sabe alto de más
 * autoriza carga que la aeronave no puede sostener. Cuatro de los cinco están
 * en la columna de 20 °C: conviene releer esa columna entera del original.
 *
 * HUECOS QUE NO SE RELLENAN
 * -------------------------
 * A 25 °C y 30 °C la tabla dice N/A a 15 000 ft pero da valores a 16 500. Eso
 * es incoherente al revés —falta un dato que debería existir— y esa dirección
 * es segura: la app dice "fuera de carta" y no autoriza nada. No se inventan.
 */
const IGE_ALTS = [
  1000, 3000, 5000, 8000, 10000, 13000, 15000,
  16500, 17000, 18000, 18500, 19500, 20000, 21000, 22000,
];
const IGE_TEMPS = [10, 15, 20, 25, 30, 35];
const IGE_ROWS = [
  [2250, 2250, 2250, 2250, 2250, 2250], //  1 000 ft
  [2250, 2250, 2250, 2250, 2250, 2250], //  3 000 ft
  [2250, 2250, 2250, 2250, 2250, 2250], //  5 000 ft
  [2250, 2250, 2250, 2250, 2250, 2250], //  8 000 ft
  [2250, 2250, 2250, 2250, 2160, null], // 10 000 ft
  [2120, 2040, 1980, 1950, null, null], // 13 000 ft
  [1920, 1830, null, null, null, null], // 15 000 ft — 20 °C anulada (decía 2250)
  [1800, 1650, null, 1550, 1520, null], // 16 500 ft — 20 °C anulada (decía 1675)
  [1770, 1620, 1580, 1540, 1500, null], // 17 000 ft
  [1700, 1550, 1500, 1420, null, null], // 18 000 ft
  [1650, 1500, 1460, 1390, 1330, null], // 18 500 ft
  [1630, 1420, null, null, null, null], // 19 500 ft — 20 °C anulada (decía 1430)
  [1470, 1420, 1390, null, null, null], // 20 000 ft
  [1410, 1330, null, null, null, null], // 21 000 ft — 20 °C anulada (decía 1350)
  [null, null, null, null, null, null], // 22 000 ft — 10 °C anulada (decía 1430)
];
/** Tabla publicada, no lectura de nomograma. */
const IGE_IS_ESTIMATE = false;

const FUEL_ALTS = [0, 2000, 4000, 6000, 8000, 10000, 12000, 14000, 16000, 18000];
const FUEL_TEMPS = [-20, -15, -10, -5, 0, 5, 10, 15, 20, 25, 30, 35, 40];
const FUEL_ROWS = [
  [170,171,172,173,174,175,175,176,174,170,165,162,158],
  [168,169,170,170,171,172,168,165,162,158,155,151,147],
  [167,167,168,167,164,161,158,155,152,148,145,140,137],
  [165,161,159,155,153,150,147,145,142,138,135,130,127],
  [154,150,148,145,142,140,137,134,132,127,124,121,null],
  [142,140,138,135,133,131,127,124,122,118,114,null,null],
  [132,130,127,125,122,120,117,114,112,108,null,null,null],
  // 14 000 ft. El valor a +25 °C figuraba como 145 l/h en una serie que venía
  // bajando 122→102: un salto del 40 % sin causa física, error de digitalización.
  // Se deja SIN DATO en vez de corregirlo por inferencia — la app avisa que hay
  // que ir al RFM en lugar de dar un número que nadie verificó.
  [122,119,117,115,113,111,108,105,102,null,null,null,null],
  [112,110,108,106,104,102,99,97,null,null,null,null,null],
  [103,101,99,97,95,93,91,89,null,null,null,null,null],
];

export const CARTA_OGE_AS350B3 = {
  id: "as350b3.oge",
  titulo: "5.5 HOVER OUT OF GROUND EFFECT",
  magnitud: "peso_max_kg",
  alts: OGE_ALTS,
  temps: OGE_TEMPS,
  rows: OGE_ROWS,
  procedencia: {
    fuente: "RFM AS350 B3 — carta OGE",
    tipo: "tabla",
    verificada: false,
    nota: "Transcripción manual sin cotejo celda por celda contra el manual.",
  },
};

/**
 * Peso máximo admisible en estacionario DENTRO de efecto suelo (IGE), en kg.
 *
 * Atención: en el manual esta carta es un nomograma de líneas diagonales de
 * OAT, no una grilla de celdas. Lo de abajo es una lectura visual aproximada.
 */
export const CARTA_IGE_AS350B3 = {
  id: "as350b3.ige",
  titulo: "6 HOVER IN GROUND EFFECT — IGE Hovering Flight Performance (Figura 5)",
  magnitud: "peso_max_kg",
  alts: IGE_ALTS,
  temps: IGE_TEMPS,
  rows: IGE_ROWS,

  /**
   * CONDICIONES DE LA CARTA, copiadas del encabezado de la Figura 5 del RFM.
   *
   * No son letra chica: cambian el resultado. La más importante es la última,
   * que la aplicación no modelaba en absoluto — con el faro de búsqueda o
   * equipo calefaccionado encendidos se pasa fácil de 50 A, y el peso máximo
   * real baja 50 kg respecto del que muestra la carta.
   */
  condiciones: {
    viento: "Sin viento",
    sistemas: "Calefacción y desempañador APAGADOS",
    dominioHp: { minFt: 0, maxFt: 23000 },
    dominioOat: { minC: -40, maxSobreIsaC: 35 },
    alturaEstacionarioFt: 5,
    potencia: "Máxima de despegue",
    /** Con consumo eléctrico sobre 50 A, el peso máximo baja 50 kg. */
    correccionElectrica: { sobreAmperios: 50, restaKg: 50 },
  },

  procedencia: {
    fuente:
      "Tabla de Peso Máximo por Aeronave del operador (AS 350-B3 Plus / Arriel 2B1), " +
      "cotejada contra la Figura 5 del RFM",
    tipo: "tabla_del_operador",
    verificada: false,
    nota:
      "Los VALORES vienen de la tabla del operador, no de leer el nomograma. La " +
      "Figura 5 del RFM sirvió para confirmar las condiciones de la carta y para " +
      "descartar la celda de 15 000 ft / 20 °C, pero un nomograma escaneado no se " +
      "puede digitalizar punto por punto con fiabilidad: eso sigue pendiente. " +
      "La carta del manual cubre de 0 a 23 000 ft y de −40 °C a ISA+35; la tabla " +
      "del operador solo una ventana de +10 a +35 °C y de 1 000 a 15 000 ft.",
  },
};

/**
 * Consumo de combustible, en kg/h, según altitud presión y temperatura.
 */
export const CARTA_CONSUMO_AS350B3 = {
  id: "as350b3.consumo",
  titulo: "Consumo en crucero",
  magnitud: "consumo_kg_h",
  alts: FUEL_ALTS,
  temps: FUEL_TEMPS,
  rows: FUEL_ROWS,
  procedencia: {
    fuente: "RFM AS350 B3 — tabla de consumo",
    tipo: "tabla",
    verificada: false,
    nota: "Contiene al menos una anomalía detectada — ver anomaliasConocidas.",
  },
};

/**
 * Anomalías ya detectadas por la auditoría automática y todavía sin resolver
 * contra el RFM. Sirven de recordatorio activo, no de corrección.
 */
export const ANOMALIAS_CONOCIDAS = [
  {
    carta: "as350b3.consumo",
    alt: 14000,
    temp: 25,
    valor: null,
    esperadoAproximado: 99,
    descripcion:
      "La serie de 14 000 ft desciende de forma sostenida (122 → 102) y en +25 °C " +
      "figuraba 145 kg/h: un salto del 40 % sin causa física, error de " +
      "digitalización. La celda quedó SIN DATO en vez de corregirse por " +
      "inferencia — la app avisa que hay que ir al RFM en lugar de dar un " +
      "número que nadie verificó.",
    estado: "celda anulada; pendiente de leer el valor real en el RFM",
  },
  {
    carta: "as350b3.ige",
    alt: 15000,
    temp: 20,
    valor: null,
    esperadoAproximado: 1740,
    descripcion:
      "La tabla del operador da 2250 kg a 15 000 ft y 20 °C. No puede ser: la " +
      "serie de esa altitud viene bajando (1920 → 1830) y 2250 es justamente el " +
      "peso máximo estructural, repetido en todas las filas bajas. Serían 510 kg " +
      "por encima de la tendencia y 270 más que a 13 000 ft con la misma " +
      "temperatura. La Figura 5 del RFM lo confirma: a 15 000 ft la curva de " +
      "+20 °C queda muy a la izquierda de 2250 kg, que en esa carta es el " +
      "extremo derecho del eje de pesos. La celda quedó ANULADA en vez de " +
      "corregirse por inferencia: la app pide ir al manual en lugar de dar un " +
      "número que nadie verificó.",
    estado: "confirmado erróneo contra el RFM; celda anulada, falta leer el valor real",
  },
  {
    carta: "as350b3.ige",
    alt: null,
    temp: null,
    valor: null,
    esperadoAproximado: null,
    descripcion:
      "La meseta artificial de 1650 kg sobre 19 000 ft desapareció al reemplazar " +
      "la carta estimada de nomograma por la tabla publicada del operador. La " +
      "tabla nueva llega hasta 15 000 ft: por encima no hay dato, y la app lo " +
      "declara en vez de extrapolar.",
    estado: "resuelto al cambiar la fuente de la carta",
  },
];

/**
 * Perfil completo de la aeronave: brazos, envolvente de CG y límites.
 *
 * La envolvente sí viene impresa del manual (Fig. 2-1). El único tramo
 * estimado está marcado abajo.
 */
/**
 * Compartimentos de carga del AS350 B3.
 *
 * Una sola definición para toda la app: estaba duplicada acá y en el legado, y
 * agregar la canasta en un solo lado habría dejado dos listas distintas de
 * dónde se puede poner equipaje.
 *
 * La CANASTA es un compartimento más, no un accesorio exclusivo de una
 * matrícula: figura siempre en la lista y cualquier pasajero puede asignarle su
 * equipaje. Comparte estación de centrado con el compartimento derecho, así que
 * usa el mismo brazo. Lo que cambia según esté instalada o no es el PESO VACÍO
 * de la aeronave, que es otra cosa y se resuelve en la enmienda de peso y
 * balance de cada matrícula.
 */
export const COMPARTIMENTOS_AS350B3 = [
  { key: "derecha", label: "Derecha", maxKg: 100, armKey: "bagLat" },
  { key: "izquierda", label: "Izquierda", maxKg: 120, armKey: "bagLat" },
  { key: "canasta", label: "Canasta (derecha)", maxKg: 100, armKey: "bagLat" },
  { key: "trasero", label: "Trasero", maxKg: 80, armKey: "bagRear" },
];

export const PERFIL_AS350B3 = {
  id: "as350b3",
  tipo: "Airbus AS350 B3",
  emptyKg: 1300,
  emptyArm: 3.52,
  arms: {
    pilot: 1.55,
    paxFront: 1.55,
    paxRear: 2.54,
    bagLat: 3.2,
    bagRear: 4.6,
    external: 3.4,
    fuel: 3.47,
  },
  fuelDensity: 0.8,
  maxInternalKg: 2250,
  maxExternalKg: 2800,
  techoServicioFt: 23000,
  datumNote: "Datum longitudinal X: 3.40 m (133.8 in) adelante del eje del rotor principal — todas las estaciones y el CG se miden desde ahí. Datum vertical Z: 2.60 m (102.3 in) sobre el piso de cabina. Datum lateral Y: plano de simetría, izquierda negativa y derecha positiva. (RFM AS350 B3e, sección 6.2.2, figura 6-1.) El datum define desde dónde se mide, no cuánto pesa ni dónde está el CG de esta matrícula: eso sale del acta de pesaje.",
  ogeTable: CARTA_OGE_AS350B3,
  igeTable: CARTA_IGE_AS350B3,
  fuelTable: CARTA_CONSUMO_AS350B3,
  envelope: {
    forward: [[1200, 3.17], [2000, 3.17], [2250, 3.21], [2800, 3.29]],
    rearwardInternal: [[1200, 3.5], [1750, 3.49], [2250, 3.425]],
    // El tramo 2250 → 2800 es estimado: el manual no imprime quiebre intermedio.
    rearwardExternal: [[1200, 3.5], [1750, 3.49], [2250, 3.425], [2800, 3.43]],
  },
  cargoCompartments: COMPARTIMENTOS_AS350B3,
};

/** Plantilla vacía para dar de alta un tipo nuevo desde la app. */
export const PERFIL_EN_BLANCO = {
  tipo: "Nuevo tipo",
  emptyKg: 0,
  emptyArm: 0,
  arms: { pilot: 0, paxFront: 0, paxRear: 0, bagLat: 0, bagRear: 0, external: 0, fuel: 0 },
  fuelDensity: 0.8,
  maxInternalKg: 0,
  maxExternalKg: 0,
  techoServicioFt: 0,
  datumNote: "",
  ogeTable: null,
  igeTable: null,
  fuelTable: null,
  envelope: {
    forward: [[0, 0], [1, 0]],
    rearwardInternal: [[0, 0], [1, 0]],
    rearwardExternal: [[0, 0], [1, 0]],
  },
  cargoCompartments: [
    { key: "derecha", label: "Derecha", maxKg: 0, armKey: "bagLat" },
    { key: "izquierda", label: "Izquierda", maxKg: 0, armKey: "bagLat" },
    { key: "trasero", label: "Trasero", maxKg: 0, armKey: "bagRear" },
  ],
};


/**
 * Estaciones de asiento del AS350 B3.
 *
 * Su formato de peso y balance agrupa a los ocupantes en dos bloques —delante
 * y atrás— y así se venía calculando. Se declaran explícitamente para que el
 * manifiesto pueda construirse a partir del TIPO en lugar de tener "delante" y
 * "atras" escritos en el código: el BK117 tiene nueve estaciones y con dos
 * claves fijas no entra.
 */
export const ESTACIONES_AS350B3 = [
  { key: "delante", label: "Delante", brazoM: 1.55 },
  { key: "atras", label: "Atrás", brazoM: 2.54 },
];
